import { CloseOutlined } from '@ant-design/icons'
import { Popover, Skeleton } from 'antd'
import {
  cloneElement,
  type ReactElement,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { RepeatIcon } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/controls/SeriesIcons'
import { SERIES_TEXT_V2 } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/seriesText'

import { useFetchSeriesRunQuery } from '../api/series.api'
import { toAppointmentSeriesLink } from '../api/series.transform'
import type { AppointmentSeriesLink, SeriesRun } from '../api/series.types'

import { RUN_REFETCH_SECONDS, seriesPopoverDetails } from './seriesPopover.model'
import { clearSeriesSelection, type SeriesSelection } from './seriesSelectionStore'
import styles from './seriesPopover.module.scss'

type EditHandler = (event: IPatientSchedule) => void

type Props = {
  selection: SeriesSelection | null
  siteId: string
  children: ReactElement
  onEditOne: EditHandler
  onEditSeries: EditHandler
}

type BodyProps = Omit<Props, 'selection' | 'children'> & {
  selection: SeriesSelection
  active: boolean
}

// The anchor sits inside the card's 4px border; the design keeps 4px from the outer edge.
const CARD_BORDER = 4
const POPOVER_GAP = 4
export const POPOVER_ALIGN = { offset: [CARD_BORDER + POPOVER_GAP, 0] }
const NO_TRIGGER: never[] = []
const TICK_CLASS = { done: styles.done, current: styles.current, todo: styles.todo }
const POPOVER_ATTR = 'data-series-popover'

// Escape or a press outside every Series popover and the card clears the selection.
// Any popover counts: the calendar can keep stale copies of a card, each with its own listener.
const useDismiss = (
  contentRef: RefObject<HTMLDivElement | null>,
  appointmentId: number,
  active: boolean,
) => {
  useEffect(() => {
    if (!active) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearSeriesSelection()
    }
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Element | null
      if (contentRef.current?.contains(target)) return
      if (target?.closest?.(`[${POPOVER_ATTR}]`)) return
      if (target?.closest?.(`[data-id="Appointment_${appointmentId}"]`)) return
      clearSeriesSelection()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown, true)
    }
  }, [contentRef, appointmentId, active])
}

const RunDetails = ({
  run,
  link,
  appointmentId,
}: {
  run: SeriesRun
  link: AppointmentSeriesLink
  appointmentId: number
}) => {
  const details = seriesPopoverDetails(run, link, appointmentId)
  return (
    <>
      <div className={styles.lines}>
        {details.range && <div>{details.range}</div>}
        {details.session && <div className={styles.muted}>{details.session}</div>}
      </div>
      <div
        role='img'
        aria-label={SERIES_TEXT_V2.popoverStep(details.step, run.total)}
        className={styles.ticks}>
        {details.ticks.map((tick, index) => (
          <span key={index} data-tick={tick} className={`${styles.tick} ${TICK_CLASS[tick]}`} />
        ))}
      </div>
    </>
  )
}

const Loading = ({ link }: { link: AppointmentSeriesLink | null }) => (
  <>
    {link && (
      <div className={styles.lines}>
        <div className={styles.muted}>{SERIES_TEXT_V2.popoverStep(link.step, link.total)}</div>
      </div>
    )}
    <Skeleton active title={false} paragraph={{ rows: 2 }} />
  </>
)

const PopoverBody = ({ selection, active, siteId, onEditOne, onEditSeries }: BodyProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  useDismiss(contentRef, selection.appointmentId, active)
  const link = toAppointmentSeriesLink(selection.event.series)
  const { data, isError } = useFetchSeriesRunQuery(
    { siteId, id: selection.seriesId },
    { refetchOnMountOrArgChange: RUN_REFETCH_SECONDS, skip: !siteId },
  )

  const failed = !data && (isError || data === null || !siteId)
  const edit = (handler: EditHandler) => () => {
    clearSeriesSelection()
    handler(selection.event)
  }

  return (
    <div ref={contentRef} className={styles.content} {...{ [POPOVER_ATTR]: '' }}>
      <div className={styles.titleRow}>
        <RepeatIcon size={16} stroke={2.2} />
        <span className={styles.title}>{SERIES_TEXT_V2.popoverTitle}</span>
        <button
          type='button'
          aria-label={SERIES_TEXT_V2.popoverClose}
          className={styles.close}
          onClick={clearSeriesSelection}>
          <CloseOutlined aria-hidden />
        </button>
      </div>
      {data && link && (
        <RunDetails run={data} link={link} appointmentId={selection.appointmentId} />
      )}
      {failed && <div className={styles.failed}>{SERIES_TEXT_V2.loadFailed}</div>}
      {!data && !failed && <Loading link={link} />}
      <div className={styles.actions}>
        <button type='button' className={styles.editOne} onClick={edit(onEditOne)}>
          {SERIES_TEXT_V2.editThis}
        </button>
        <button type='button' className={styles.editSeries} onClick={edit(onEditSeries)}>
          {SERIES_TEXT_V2.editSeries}
        </button>
      </div>
    </div>
  )
}

// True while this copy's card is on the page; the calendar can keep a copy whose node it already dropped.
const useAnchorOnPage = (
  anchorRef: RefObject<HTMLElement | null>,
  selection: SeriesSelection | null,
): boolean => {
  const [onPage, setOnPage] = useState(false)
  useLayoutEffect(() => {
    // Only a node known to be detached refuses; a child that takes no ref still opens.
    setOnPage(selection !== null && anchorRef.current?.isConnected !== false)
  }, [anchorRef, selection])
  return onPage
}

// Always wraps a Series card, so selecting or clearing only toggles `open` and never remounts the card.
export const SeriesPopover = ({ selection, children, ...rest }: Props) => {
  const anchorRef = useRef<HTMLElement>(null)
  // The last selection stays drawn while the popover fades out.
  const [shown, setShown] = useState(selection)
  if (selection && selection !== shown) setShown(selection)

  // A stale copy never opens: with no node to align to, it flashes in the top-left corner as it fades.
  const open = useAnchorOnPage(anchorRef, selection) && selection !== null
  return (
    <Popover
      open={open}
      arrow={false}
      placement='rightTop'
      align={POPOVER_ALIGN}
      trigger={NO_TRIGGER}
      destroyOnHidden
      classNames={{ root: styles.root, body: styles.body }}
      content={shown ? <PopoverBody selection={shown} active={open} {...rest} /> : null}>
      {cloneElement(children as ReactElement<{ ref?: RefObject<HTMLElement | null> }>, {
        ref: anchorRef,
      })}
    </Popover>
  )
}
